import React, { useState, useEffect, useReducer, Fragment, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import 'whatwg-fetch';
import { createRoot } from 'react-dom/client';
import '@embeds/common/Root/_root.scss';

// import components
import asyncComponent from '@cms/common/Async/AsyncComponent';
import { LazyImageProvider } from '@embeds/common/Image/LazyImage/LazyImageContext';
import EnvironmentProvider, { withEnvironmentContext } from '@embeds/core/EnvironmentContext';
import BrandPanel from '@cms/common/BrandPanel/BrandPanel';
import RegistrationOverlay from '@embeds/common/Registration/Overlay';
import PromotionalText from '@cms/common/PromotionalTextPanel/PromotionalText';

// import utils
import { generateS3Key } from '@utils/embeds/generateS3Key';
import '@assets/scss/components/common/_embeds-common-iframe.scss';

import detectTheme from '@embeds/core/themes';

// The following import is not used in this component,
// however importing it here means Webpack bundles it once into intial entrypoint rather than seperately into each tool
// eslint-disable-next-line
import akamaiImageUrl from '@utils/embeds/common/imageurl';
import { EmbedProvider } from '@utils/embeds/common/context/EmbedContext';

// Load the async component for the embed type.
const loadComponent = (type, isPreview) => {

  // const isApp = Boolean(window.DNA_APP_ENV || document.querySelector('.screenkit--embed-container'));

  switch (type) {
  case 'poll': return asyncComponent( () => import('@embeds/Poll/PollEmbed') );
  case 'imageslideshow': return asyncComponent( () => import('@embeds/ImageSlideshow/ImageSlideshowEmbed') );
  case 'imagecomparison': return asyncComponent( () => import('@embeds/ImageComparison/ImageComparisonEmbed') );
  case 'quiz': return asyncComponent( () => import('@embeds/Quiz/KnowledgeQuiz/KnowledgeQuizEmbed') );
  case 'factbox': return asyncComponent( () => import('@embeds/Factbox/FactboxEmbed') );
  case 'personalityquiz': return asyncComponent( () => import('@embeds/Quiz/PersonalityQuiz/PersonalityQuizEmbed') );
  case 'timeline': return asyncComponent( () => import('@embeds/Timeline/TimelineEmbed') );
  case 'graphicimagefilter': return asyncComponent( () => import('@embeds/GraphicImageFilter/GraphicImageFilterEmbed') );
  case 'survey': return asyncComponent(
    () => isPreview ? import('@embeds/SurveyComp/SurveyCompEmbed') : import('../../zoidbergs/SurveyComp/SurveyCompParent') );
  case 'competition': return asyncComponent(
    () => isPreview ? import('@embeds/SurveyComp/SurveyCompEmbed') : import('../../zoidbergs/SurveyComp/SurveyCompParent')
  );
  case 'podcast': return asyncComponent( () => import('@embeds/Podcast/PodcastEmbed') );
  case 'imageexpander': return asyncComponent( () => import('@embeds/ImageExpander/ImageExpanderEmbed') );
  case 'toplist': return asyncComponent( () => import('@embeds/Toplist/ToplistEmbed') );
  case 'tilecollection': return asyncComponent( () => import('@embeds/TileCollection/TileCollectionEmbed') );
  case 'ecommerce': return asyncComponent( () => import('@embeds/Ecommerce/EcommerceEmbed') );
  case 'supervoter': return asyncComponent( () => import('@embeds/Supervoter/SupervoterEmbed') );
  case 'singleanswerquiz': return asyncComponent(() => import('@embeds/Singleanswerquiz/SingleanswerquizEmbed'));
  case 'table': return asyncComponent(() => import('@embeds/Table/TableEmbed'));
    // case 'competition':   return asyncComponent( () => import('./Competition') );
  default:
    console.error(`Editorial Tools: Render Error: cannot find a async component for the type: ${ type }`);
  }
};

const getComponent = (type, isPreview) => withEnvironmentContext(loadComponent(type, isPreview));

// Create a loader reducer to handle fetch actions. Also allow contextdata to be set via success to avoid unneccessary renders.
const loaderReducer = (state, action) => {
  switch (action.type) {
  case 'FETCH_INIT':
    return {
      ...state,
      isLoading: true,
      isError: false,
    };
  case 'FETCH_SUCCESS':
    return {
      ...state,
      isLoading: false,
      isError: false,
      data: action.payload,
      contextData: action.contextData
    };
  case 'FETCH_ERROR':
    return {
      ...state,
      isLoading: false,
      isError: true,
      data: action.payload,
    };
  default:
    return state;
  }
};

export const EmbedLoader = (props) => {

  const {
    env, isPreview, id, type, rootEl, previewParam, envRefreshTrigger, variant, handlePreviewTabChange
  } = props;

  // Create states
  const [ Component ] = useState(() => getComponent(type, isPreview));
  const [ minHeight, setMinHeight ] = useState(50);
  const [ retryCount, setRetryCount ] = useState(0);
  const [ theme, setTheme ] = useState( () => detectTheme(isPreview ? previewParam.url : window.location.href) );
  const [ state, dispatch ] = useReducer(loaderReducer, {
    isLoading: false,
    data: null,
    contextData: {}
  });

  // function to fetch the json data
  const fetchData = useCallback(async(envContextData) => {
    const url = generateS3Key({ id, type }, env);

    dispatch({ type: 'FETCH_INIT' });

    const data = await fetch(url)
      .then(res => res.json())
      .catch(e => {
        console.error(e, 'can\'t fetch json url', url);
        setTimeout( () => setRetryCount( fc => (fc + 1) ), 500 );
      });

    if (data) {
      // This IF block is added for old sponsor data copy into new key 'sponsorData' ref- DSET-948
      if (typeof(data.sponsor) === 'object' && !data.sponsor.hasOwnProperty('sponsorData')) {
        if (data.sponsor.hasOwnProperty('image') && data.sponsor.hasOwnProperty('link')) {
          data.sponsor.sponsorData = [ { 'image': data.sponsor.image, 'link': data.sponsor.link } ];
        } else {
          data.sponsor.sponsorData = [ { 'image': null, 'link': null, 'sponsorMessage': null } ];
        }
      }
      // Dispatch data to reducer to commit to state
      dispatch({ type: 'FETCH_SUCCESS', payload: data, contextData: envContextData });
      if (data.capiid) rootEl.setAttribute('data-capiid', data.capiid);
    } else {
      dispatch({ type: 'FETCH_ERROR' });
    }
  }, [ id, type, env, rootEl ] );

  useEffect( () => {
    // Fetch the embed data.
    fetchData();
  }, [ fetchData ]);

  useEffect( () => {
    if (retryCount > 0 && retryCount <= 10) {
      console.log('retryCount fetch', retryCount);
      setTimeout( () => fetchData(), 1000 * retryCount );
    }
  }, [ retryCount, fetchData ]);

  useEffect( () => {

    if (isPreview) {
      const newTheme = detectTheme(isPreview ? previewParam.url : window.location.href);

      if (newTheme !== theme) {
        console.log('preview panel url change - update theme', theme, newTheme, previewParam.url);
        setTheme(newTheme);
      }
    }
  }, [ previewParam.url, theme, isPreview ] );

  const containerStyles = {
    minHeight: minHeight,
    position: 'relative'
  };

  const showDefaultBrandPanel = useMemo(() => (type !== 'supervoter'), [ type ] );
  const loadSponsor = Boolean(state.data?.sponsor?.sponsorData?.filter(x => x.image).length);
  const sponsorPositionTop = state.data?.sponsor?.sponsorPosition === 'top' ? true : false;
  return (
    <Fragment>
      { state.data && Component && (
        <EnvironmentProvider initialProps={props} theme={theme} data={state.data} handleHeightChange={setMinHeight} previewParam={previewParam} envRefreshTrigger={envRefreshTrigger} handlePreviewTabChange={handlePreviewTabChange} >
          <LazyImageProvider container={rootEl}>
            <EmbedProvider data={state.data}>
              {sponsorPositionTop && showDefaultBrandPanel && loadSponsor && (<BrandPanel sponsordata={state.data.sponsor} id={state.data.id} type={state.data.type} />)}
              <div className="et-container" style={containerStyles}>
                <Component data={state.data} env={env} isPreview={isPreview} theme={theme} containerRef={rootEl} variant={variant || undefined} />
                <RegistrationOverlay data={state.data} />
              </div>
              {state.data.sponsor?.promotionalText && (<PromotionalText sponsordata={state.data.sponsor} id={state.data.id} type={state.data.type} />)}
              { !sponsorPositionTop && showDefaultBrandPanel && loadSponsor && (<BrandPanel sponsordata={state.data.sponsor} id={state.data.id} type={state.data.type} />) }
            </EmbedProvider>
          </LazyImageProvider>
        </EnvironmentProvider>
      )}
    </Fragment>
  );
};

EmbedLoader.propTypes = {
  id: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]).isRequired,
  type: PropTypes.string.isRequired,
  env: PropTypes.string,
  isPreview: PropTypes.bool.isRequired,
  previewParam: PropTypes.object.isRequired,
  handlePreviewTabChange: PropTypes.func.isRequired,
  // theme: PropTypes.string
};

EmbedLoader.defaultProps = {
  isPreview: false,
  previewParam: {},
  // theme: window.location.hostname.match('theaustralian') ? 'theaustralian' : 'network',
  env: 'prod',
  handlePreviewTabChange: (val) => console.log('EmbedLoader handlePreviewTabChange', val)
};


export default function renderEmbed(el, data) {
  el.classList.add('nca-et-rendered');
  const root = createRoot(el);

  console.log('el', el);

  root.render(
    <EmbedLoader {...data} rootEl={el} />
    , el);

}
